/**
 * The fleet screen (§A.9): all organisms side by side. Reads `fleet.json` —
 * shape verified live against the real host on 2026-09-26
 * (`src/types/incremental.ts`'s `FleetSnapshot`); renders the graceful "not
 * published yet" state when it is absent.
 *
 * Orchestrator course correction (2026-09-28): "not two small cards on an
 * empty page" — a summary header (organisms alive/stalled, combined NAV,
 * combined day P&L) sits above a grid of large, information-rich cards
 * (status pill in words, NAV + a larger equity sparkline, day P&L,
 * drawdown, last decision, heartbeat age with the stall explanation).
 * Everything synchronous from `fleet.json` renders immediately; day P&L,
 * last decision, and the sparkline each need one more fetch per organism
 * (`equityCurve()`/`events()`), so they patch in progressively — the grid
 * never waits on every organism's network round trip to show its first
 * frame. `src/data/fleetStatus.ts` carries the shared, unit-tested
 * heartbeat-status derivation (also used by the fleet-bar switcher's
 * status dot); `src/data/fleetSummary.ts` carries the header's aggregation.
 */

import { loadFleetSnapshot } from "../data/fleet";
import type { FleetOrganismEntry } from "../types/incremental";
import { config, roleLabel } from "../config";
import { renderNotPublishedYet } from "../panels/NotPublishedYet";
import type { Router } from "../router/router";
import { attachTooltip } from "../ui/tooltip";
import { renderScreenHeader } from "./screenHeader";
import { freshnessLabel } from "../data/polling";
import { fleetStatus } from "../data/fleetStatus";
import { combinedDayPnl, combinedNav, fleetCounts } from "../data/fleetSummary";
import {
  cappedPercent,
  formatMagnitudePercent,
  formatMoney,
  formatPercent,
  formatSignedMoney,
  formatSignedPercent,
  toneClass,
  type ToneText,
} from "../format/number";
import { getRoleSnapshot } from "../data/RoleSnapshotRegistry";
import { renderSparklineSvg } from "../charts/sparklineElement";
import { latestByKind } from "../brain/sessionStatus";
import { computeDayPnl } from "../panels/vitals";
import { fetchSizingSafely } from "../panels/sizingView";
import {
  FLEET_CAPITAL_ALERT_TOOLTIP,
  STAGE_TOOLTIP,
  compactStageText,
  fleetCapitalAlertText,
  stageText,
} from "../panels/lifeView";
import {
  fetchBenchmarksSafely,
  fleetBenchmarkSummary,
  type FleetBenchmarkSummary,
} from "../panels/benchmarksView";

const DRAWDOWN_BAR_CAP = 0.25; // visual cap only — the adjacent text always shows the real number

interface OrganismDetail {
  readonly navSeries: readonly number[];
  readonly dayPnl: number | null;
  readonly lastDecisionEventTime: string | null;
  readonly lastDecisionMode: string | null;
  /** R3 ("observatory visibility of smoother state") — the book's gross
   * exposure and its cap, from `index.json.refs.sizing`; both `null` until
   * this organism has recorded at least one `sizing.smoother_state` event. */
  readonly grossExposure: number | null;
  readonly grossExposureCap: number | null;
  /** BEN — return vs buy-and-hold since genesis, max drawdown, rolling gate
   * (`index.json.refs.benchmarks`); `null` until published. */
  readonly benchmark: FleetBenchmarkSummary | null;
}

export class FleetScreen {
  private disposed = false;
  /** OQA: the fleet screen rendered once — an open tab kept the first NAV,
   * status and heartbeat forever. It re-checks fleet.json on the poll cadence and
   * re-renders when it changed. */
  private refreshTimer: number | null = null;
  private lastFleetJson: string | null = null;
  private summaryEl: HTMLDivElement | null = null;
  private dayPnlByRole = new Map<string, number | null>();

  constructor(
    private readonly container: HTMLElement,
    private readonly router: Router,
  ) {}

  async mount(): Promise<void> {
    if (this.refreshTimer === null) {
      this.refreshTimer = window.setInterval(
        () => void this.refreshIfChanged(),
        config.pollIntervalMs,
      );
    }
    this.container.innerHTML = "";
    const fleet = await loadFleetSnapshot();
    this.lastFleetJson = fleet ? JSON.stringify(fleet) : null;
    if (!fleet) {
      this.container.appendChild(
        renderNotPublishedYet(
          "Fleet screen",
          "fleet.json has not been published yet. Once published, this screen shows a summary " +
            "header and one card per organism: NAV, day P&L, drawdown, stage, last decision, and " +
            "last-heartbeat time.",
        ),
      );
      return;
    }

    this.container.appendChild(
      renderScreenHeader(
        "Fleet",
        "Every known organism side by side — status, NAV, day P&L, drawdown, last decision, and last heartbeat.",
      ),
    );

    const now = Date.now();
    this.summaryEl = this.renderSummary(fleet.organisms, now);
    this.container.appendChild(this.summaryEl);

    const grid = document.createElement("div");
    grid.className = "obs-fleet-grid";
    const cardParts = new Map<string, ReturnType<FleetScreen["renderCard"]>>();
    for (const organism of fleet.organisms) {
      const parts = this.renderCard(organism, now);
      cardParts.set(organism.role, parts);
      grid.appendChild(parts.card);
    }
    this.container.appendChild(grid);

    // Day P&L, last decision, and the sparkline all need one more fetch per
    // organism — patched in progressively (see this file's doc comment) so
    // the grid's first paint never waits on every organism's network round
    // trip. A fetch failure/absence for one organism never blocks the rest.
    void Promise.all(
      fleet.organisms
        .filter((o) => o.started)
        .map(async (organism) => {
          const detail = await this.loadDetail(organism);
          if (this.disposed) return;
          this.dayPnlByRole.set(organism.role, detail.dayPnl);
          this.patchCard(cardParts.get(organism.role), detail, now);
          this.refreshSummaryDayPnl(fleet.organisms);
        }),
    );
  }

  private renderSummary(organisms: readonly FleetOrganismEntry[], now: number): HTMLDivElement {
    const counts = fleetCounts(organisms, now);
    const nav = combinedNav(organisms);

    const panel = document.createElement("div");
    panel.className = "obs-fleet-summary";

    const countsEl = document.createElement("div");
    countsEl.className = "obs-fleet-summary-stat";
    const countsLabel = document.createElement("span");
    countsLabel.className = "obs-fleet-card-stat-label";
    countsLabel.textContent = "Organisms";
    const countsValue = document.createElement("span");
    countsValue.className = "obs-fleet-summary-value";
    countsValue.textContent = `${counts.alive} active`;
    if (counts.stalled > 0) {
      const stalledSpan = document.createElement("span");
      stalledSpan.className = "obs-value-down";
      stalledSpan.textContent = ` · ${counts.stalled} stalled`;
      countsValue.appendChild(stalledSpan);
    }
    if (counts.stopped > 0) {
      countsValue.appendChild(document.createTextNode(` · ${counts.stopped} stopped`));
    }
    if (counts.notStarted > 0) {
      countsValue.appendChild(document.createTextNode(` · ${counts.notStarted} not started`));
    }
    attachTooltip(
      countsEl,
      "Active = a heartbeat in the last 5 minutes. Stalled = running, but no heartbeat in over 5 minutes. Stopped = the runtime unit is not running. Not started = never run a cycle, or started with no heartbeat yet.",
    );
    countsEl.appendChild(countsLabel);
    countsEl.appendChild(countsValue);

    const navEl = document.createElement("div");
    navEl.className = "obs-fleet-summary-stat";
    const navLabel = document.createElement("span");
    navLabel.className = "obs-fleet-card-stat-label";
    navLabel.textContent = "Combined NAV";
    const navValue = document.createElement("span");
    navValue.className = "obs-fleet-summary-value obs-num-display";
    navValue.textContent = formatMoney(nav);
    attachTooltip(navEl, "Sum of every organism's own published net asset value.");
    navEl.appendChild(navLabel);
    navEl.appendChild(navValue);

    const pnlEl = document.createElement("div");
    pnlEl.className = "obs-fleet-summary-stat";
    pnlEl.dataset["role"] = "day-pnl";
    const pnlLabel = document.createElement("span");
    pnlLabel.className = "obs-fleet-card-stat-label";
    pnlLabel.textContent = "Combined day P&L";
    const pnlValue = document.createElement("span");
    pnlValue.className = "obs-fleet-summary-value";
    pnlValue.textContent = "–";
    attachTooltip(
      pnlEl,
      "Sum of each started organism's day P&L (latest NAV minus its first equity-curve row of the current UTC day) — fills in as each organism's own curve loads.",
    );
    pnlEl.appendChild(pnlLabel);
    pnlEl.appendChild(pnlValue);

    panel.appendChild(countsEl);
    panel.appendChild(navEl);
    panel.appendChild(pnlEl);
    return panel;
  }

  private refreshSummaryDayPnl(organisms: readonly FleetOrganismEntry[]): void {
    const pnlEl = this.summaryEl?.querySelector<HTMLElement>(
      '[data-role="day-pnl"] .obs-fleet-summary-value',
    );
    if (!pnlEl) return;
    const started = organisms.filter((o) => o.started).length;
    const resolved = this.dayPnlByRole.size;
    const total = combinedDayPnl(this.dayPnlByRole);
    const result = formatSignedMoney(total);
    pnlEl.textContent = resolved < started ? `${result.text} so far` : result.text;
    pnlEl.className = `obs-fleet-summary-value ${toneClass(result.tone)}`;
  }

  private async loadDetail(organism: FleetOrganismEntry): Promise<OrganismDetail> {
    const empty: OrganismDetail = {
      navSeries: [],
      dayPnl: null,
      lastDecisionEventTime: null,
      lastDecisionMode: null,
      grossExposure: null,
      grossExposureCap: null,
      benchmark: null,
    };
    try {
      const role = await getRoleSnapshot({
        role: organism.role,
        organismId: organism.organism_id,
        displayName: roleLabel(organism.role),
        path: organism.organism_id,
      });
      if (!role) return empty;
      // `fetchSizingSafely` (code review finding) isolates a sizing-only
      // fetch failure to a `null` result — without it, a rejected
      // `role.sizing()` promise would fail this WHOLE `Promise.all`,
      // blanking this card's NAV sparkline/day-P&L/last-decision too, none
      // of which have anything to do with sizing.
      const [curve, events, sizing, benchmarks] = await Promise.all([
        role.equityCurve(),
        role.events(),
        fetchSizingSafely(role),
        fetchBenchmarksSafely(role),
      ]);
      const navSeries = curve
        .map((row) => (row.nav !== null ? Number.parseFloat(row.nav) : Number.NaN))
        .filter((n) => Number.isFinite(n));
      const dayPnl = computeDayPnl(curve);
      const latestWake = latestByKind(
        events.filter((event) => Boolean(event.payload)),
        "brain.wake",
      );
      const mode = latestWake?.payload?.["mode"];
      return {
        navSeries,
        dayPnl,
        lastDecisionEventTime: latestWake?.eventTime ?? null,
        lastDecisionMode: typeof mode === "string" ? mode : null,
        grossExposure: sizing?.gross_current_weight ?? null,
        grossExposureCap: sizing?.gross_cap_weight ?? null,
        benchmark: fleetBenchmarkSummary(benchmarks),
      };
    } catch {
      return empty; // a fetch failure never blocks the rest of the fleet screen
    }
  }

  private patchCard(
    parts: ReturnType<FleetScreen["renderCard"]> | undefined,
    detail: OrganismDetail,
    now: number,
  ): void {
    if (!parts) return;
    if (detail.navSeries.length > 0) {
      const spark = renderSparklineSvg(detail.navSeries, {
        width: 220,
        height: 56,
        padding: 4,
        endDot: true,
        tooltip: `NAV over the last ${detail.navSeries.length} recorded cycles.`,
      });
      if (spark) parts.navRow.appendChild(spark);
    }

    const pnl = formatSignedMoney(detail.dayPnl);
    parts.dayPnlValue.textContent = pnl.text;
    parts.dayPnlValue.classList.add(toneClass(pnl.tone));

    if (detail.lastDecisionEventTime) {
      const ms = Date.parse(detail.lastDecisionEventTime);
      const relative = Number.isFinite(ms) ? freshnessLabel(ms, now) : detail.lastDecisionEventTime;
      parts.lastDecisionValue.textContent = detail.lastDecisionMode
        ? `${relative} · ${detail.lastDecisionMode}`
        : relative;
    } else {
      parts.lastDecisionValue.textContent = "no wake recorded yet";
    }

    // R3: stays the initial "–" (never a fabricated 0%) when this organism
    // has never recorded a sizing.smoother_state event.
    if (detail.grossExposure !== null && detail.grossExposureCap !== null) {
      parts.grossExposureValue.textContent = `${formatPercent(detail.grossExposure)} of ${formatPercent(detail.grossExposureCap, 0)}`;
    }

    // BEN: one line — return vs buy-and-hold, max drawdown, rolling gate.
    const bench = detail.benchmark;
    if (bench) {
      const excess = formatSignedPercent(bench.excessVsBuyAndHold);
      const dd = formatMagnitudePercent(bench.maxDrawdown);
      parts.benchmarkValue.textContent = excess.text;
      parts.benchmarkValue.classList.add(toneClass(excess.tone));
      parts.episodeDrawdownValue.textContent = dd.text;
      parts.episodeDrawdownValue.classList.add(toneClass(dd.tone));
      parts.gateValue.textContent = bench.gate.text;
      parts.gateValue.classList.add(bench.gate.toneClass);
    }
  }

  private renderCard(
    organism: FleetOrganismEntry,
    now: number,
  ): {
    card: HTMLDivElement;
    navRow: HTMLDivElement;
    dayPnlValue: HTMLSpanElement;
    lastDecisionValue: HTMLSpanElement;
    grossExposureValue: HTMLSpanElement;
    benchmarkValue: HTMLSpanElement;
    episodeDrawdownValue: HTMLSpanElement;
    gateValue: HTMLSpanElement;
  } {
    const status = fleetStatus(organism, now);
    const card = document.createElement("div");
    card.className = "obs-fleet-card";
    if (!organism.started) card.classList.add("obs-fleet-card-inactive");
    // OQA: a stopped organism's page is still real (its own connectome, its last
    // published book, a fresh episode's genesis) — open it from the card too.
    const navigable = organism.started || organism.nav !== null || Boolean(organism.episode_status);
    if (status.freshness === "stale") card.classList.add("obs-fleet-card-stalled");
    // Keyboard accessibility (design self-review round 1): the table rows
    // this card replaced weren't keyboard-reachable either, but a card grid
    // reads more like a set of navigable tiles than a table ever did, so a
    // plain click-only `<div>` is a sharper regression here. A started
    // organism's card is a real navigation target — give it a tab stop,
    // a button role/label, and an Enter/Space activator to match.
    if (navigable) {
      card.tabIndex = 0;
      card.setAttribute("role", "button");
      card.setAttribute("aria-label", `Open ${roleLabel(organism.role)}`);
      card.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.router.navigate({ kind: "organism", role: organism.role });
        }
      });
    }

    const top = document.createElement("div");
    top.className = "obs-fleet-card-top";
    const dot = document.createElement("span");
    dot.className = `obs-fleet-status-dot ${status.dotClass}`;
    const name = document.createElement("span");
    name.className = "obs-fleet-card-name";
    name.textContent = roleLabel(organism.role);
    const statusPill = document.createElement("span");
    statusPill.className = "obs-fleet-status-pill";
    statusPill.textContent = status.word;
    attachTooltip(statusPill, status.tooltip);
    top.appendChild(dot);
    top.appendChild(name);
    top.appendChild(statusPill);
    // D6: W below the capital-alert line (alarm only) — a pill on the card itself.
    const alertText = fleetCapitalAlertText(organism.capital_alert);
    if (alertText) {
      const alertPill = document.createElement("span");
      alertPill.className = `obs-fleet-status-pill ${toneClass("down")}`;
      alertPill.dataset["testid"] = "fleet-capital-alert";
      alertPill.textContent = alertText;
      attachTooltip(alertPill, FLEET_CAPITAL_ALERT_TOOLTIP);
      top.appendChild(alertPill);
    }
    card.appendChild(top);

    const navRow = document.createElement("div");
    navRow.className = "obs-fleet-card-nav";
    const navValue = document.createElement("span");
    navValue.className = "obs-fleet-card-nav-value obs-num-display";
    navValue.textContent = formatMoney(organism.nav);
    attachTooltip(navValue, "Net asset value from the latest portfolio.snapshot (NAV).");
    navRow.appendChild(navValue);
    card.appendChild(navRow);
    if (status.kind === "pending" || organism.episode_status === "pending_first_wake") {
      const episodeNote = document.createElement("div");
      episodeNote.className = "obs-panel-hint";
      episodeNote.textContent = "Fresh episode · flat at its opening capital · awaiting first wake";
      attachTooltip(
        episodeNote,
        "The previous episode was archived; this organism's new book has not traded yet. " +
          "NAV is the recorded opening capital (its actual broker equity at the fresh start).",
      );
      card.appendChild(episodeNote);
    }

    // Computed once, reused for both the drawdown bar's tooltip and the
    // stat-row cell below — was two independent formatMagnitudePercent
    // calls on the same value (code-review finding, revamp self-review
    // round 1).
    const drawdown = organism.drawdown !== null ? formatMagnitudePercent(organism.drawdown) : null;

    if (organism.drawdown !== null && drawdown) {
      const bar = document.createElement("div");
      bar.className = "obs-fleet-drawdown-bar";
      const fill = document.createElement("div");
      fill.className = "obs-fleet-drawdown-bar-fill";
      const magnitude = Math.abs(organism.drawdown);
      fill.style.width = `${cappedPercent(magnitude, DRAWDOWN_BAR_CAP).toFixed(1)}%`;
      bar.appendChild(fill);
      attachTooltip(bar, `Drawdown from the running peak NAV: ${drawdown.text}.`);
      card.appendChild(bar);
    }

    const grid = document.createElement("div");
    grid.className = "obs-fleet-card-grid";

    const dayPnlStat = this.cardStat("Day P&L", "–");
    grid.appendChild(dayPnlStat);
    const dayPnlValue = dayPnlStat.querySelector<HTMLSpanElement>(".obs-fleet-card-stat-value")!;

    grid.appendChild(this.drawdownStat(drawdown));

    // R3 ("observatory visibility of smoother state") — patched in
    // alongside Day P&L/Last decision once this organism's own
    // index.json.refs.sizing resolves (loadDetail/patchCard below).
    const grossExposureStat = this.cardStat("Gross exposure", "–");
    attachTooltip(
      grossExposureStat,
      "Sum of every attended symbol's own marked notional, divided by NAV, against the whole-book gross-exposure cap.",
    );
    grid.appendChild(grossExposureStat);
    const grossExposureValue = grossExposureStat.querySelector<HTMLSpanElement>(
      ".obs-fleet-card-stat-value",
    )!;

    const lastDecisionStat = this.cardStat("Last decision", "–");
    grid.appendChild(lastDecisionStat);
    const lastDecisionValue = lastDecisionStat.querySelector<HTMLSpanElement>(
      ".obs-fleet-card-stat-value",
    )!;

    const benchmarkStat = this.cardStat("vs Buy & hold", "–");
    attachTooltip(
      benchmarkStat,
      "Live return minus equal-weight buy-and-hold since the episode's genesis (after all costs), and the live max drawdown — from the Benchmarks panel.",
    );
    grid.appendChild(benchmarkStat);
    const benchmarkValue = benchmarkStat.querySelector<HTMLSpanElement>(
      ".obs-fleet-card-stat-value",
    )!;
    const episodeDrawdownStat = this.cardStat("Max DD (episode)", "–");
    attachTooltip(
      episodeDrawdownStat,
      "Largest peak-to-trough fall of W(t) since the episode's genesis, over every sample.",
    );
    grid.appendChild(episodeDrawdownStat);
    const episodeDrawdownValue = episodeDrawdownStat.querySelector<HTMLSpanElement>(
      ".obs-fleet-card-stat-value",
    )!;
    const gateStat = this.cardStat("Rolling gate", "–");
    attachTooltip(
      gateStat,
      "The four backtest-gate criteria on the live series over the trailing 30 days; insufficient data until 20 counted days.",
    );
    grid.appendChild(gateStat);
    const gateValue = gateStat.querySelector<HTMLSpanElement>(".obs-fleet-card-stat-value")!;

    // OBS2: an unbound life says why (its regime-binding progress), not a bare word.
    const stageStat = this.cardStat("Stage", compactStageText(organism.stage, organism.stage_note));
    attachTooltip(stageStat, `${stageText(organism.stage, organism.stage_note)}. ${STAGE_TOOLTIP}`);
    grid.appendChild(stageStat);

    const heartbeatText = organism.last_heartbeat_time
      ? status.heartbeatMs !== null
        ? freshnessLabel(status.heartbeatMs, now)
        : organism.last_heartbeat_time
      : "–";
    const heartbeatStat = this.cardStat("Last heartbeat", heartbeatText);
    if (organism.last_heartbeat_time) heartbeatStat.title = organism.last_heartbeat_time;
    attachTooltip(heartbeatStat, status.tooltip);
    grid.appendChild(heartbeatStat);
    card.appendChild(grid);

    if (navigable) {
      card.addEventListener("click", () =>
        this.router.navigate({ kind: "organism", role: organism.role }),
      );
    }
    return {
      card,
      navRow,
      dayPnlValue,
      lastDecisionValue,
      grossExposureValue,
      benchmarkValue,
      episodeDrawdownValue,
      gateValue,
    };
  }

  private drawdownStat(drawdown: ToneText | null): HTMLDivElement {
    const stat = this.cardStat("Drawdown", drawdown?.text ?? "–");
    if (drawdown) {
      stat.querySelector(".obs-fleet-card-stat-value")?.classList.add(toneClass(drawdown.tone));
    }
    return stat;
  }

  private cardStat(labelText: string, valueText: string): HTMLDivElement {
    const stat = document.createElement("div");
    stat.className = "obs-fleet-card-stat";
    const label = document.createElement("span");
    label.className = "obs-fleet-card-stat-label";
    label.textContent = labelText;
    const value = document.createElement("span");
    value.className = "obs-fleet-card-stat-value";
    value.textContent = valueText;
    stat.appendChild(label);
    stat.appendChild(value);
    return stat;
  }

  private async refreshIfChanged(): Promise<void> {
    if (this.disposed) return;
    let fleet;
    try {
      fleet = await loadFleetSnapshot();
    } catch {
      return; // transient: keep what is on screen, retry next tick
    }
    const serialized = fleet ? JSON.stringify(fleet) : null;
    if (this.disposed || serialized === this.lastFleetJson) return;
    this.dayPnlByRole = new Map();
    await this.mount();
  }

  dispose(): void {
    this.disposed = true;
    if (this.refreshTimer !== null) window.clearInterval(this.refreshTimer);
    this.refreshTimer = null;
  }
}
